import { useState, useEffect } from "react";

import { toNaturalSentence } from "../../../../utils/string";
import { serializeError } from "../../../../utils/serializeError";
import { userSelectors } from "../../../../redux/user/userSelectors";
import { useDispatch, useSelector } from "react-redux";
import { CloneAPIModal } from "../components/dataPages/CloneAPIModal";
import { CodeEditor } from "../../../components/CodeEditor/CodeEditor";
import { ApplicableRegulationsDescriptions } from "../components/dataPages/ApplicableRegulationsTags";
import { sendGaEvent } from "../../../../config/userTracking";
import { apiTemplatesCatalogApi } from "../../../../api/apiTemplatesCatalogApi";
import { notEmptyArray } from "../../../../utils/helpers";
import { SchemaTable } from "../components/tables/SchemaTable";
import { InputsTable } from "../components/tables/InputsTable";
import { PublicBlock } from "../PublicComponents/PublicBlock";
import { Loader } from "../../../components/Loader/Loader";
import { MessageBox } from "../../../components/MessageBox/MessageBox";
import { LinkButton } from "../../../components/buttons/LinkButton";
import { PlatformHead } from "../../../layout/PlatformHead";
import { DataPageHeader } from "../components/dataPages/DataPageHeader";
import { DataPageAbout } from "../components/dataPages/DataPageAbout";
import { DataPageGridItems } from "../components/dataPages/DataPageGridItems";
import { linksConstants } from "../../../../constants/linksConstants";
import { userActions } from "../../../../redux/user/userSlice";
import { slugify } from "../../../../utils/slugify";
import Axios from "axios";
import { useRouter } from "next/router";

export const ApiTemplatePage = ({ templateId }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const userData = useSelector(userSelectors.getUserData);
  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn);
  const userPermissions = useSelector(userSelectors.getUserPermissions);
  const canCloneApiTemplates = userPermissions?.clone_api_template;
  const [cloneAPIModalOpen, setCloneAPIModalOpen] = useState(false);
  const [templateData, setTemplateData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedRecipes, setRelatedRecipes] = useState([]);
  const [inputsData, setInputsData] = useState([]);
  const [outputData, setOutputData] = useState({});
  const [canonical, setCanonical] = useState("");
  const [title, setTitle] = useState("");

  const openCloneAPIModal = () => {
    sendGaEvent(
      {
        action: `Open Create Modal from Recipe "${templateData?.name}"`,
        category: "Create Data API",
      },
      userData?.user?.email,
    );
    setCloneAPIModalOpen(true);
  };

  const closeCloneAPIModal = () => {
    setCloneAPIModalOpen(false);
  };

  const createInputData = ({ inputs, recommended, schemas, schemasProviders }) => {
    let arrayOfRecommendedInputs = [];
    let arrayOfOptionalInputs = [];
    inputs.forEach(item => {
      const isRecommended = recommended.indexOf(item.name) !== -1;
      isRecommended
        ? arrayOfRecommendedInputs.push({
            description: item.description || "",
            type: item.type || "",
            name: item.name || "",
            example: "",
            isRecommended: true,
          })
        : arrayOfOptionalInputs.push({
            description: item.description || "",
            type: item.type || "",
            name: item.name || "",
            example: "",
            isRecommended: false,
          });
    });

    const arrayOfInputs = [...arrayOfRecommendedInputs, ...arrayOfOptionalInputs];

    let returnedObject = {
      latest_version: {
        input_fields: arrayOfInputs,
        schema: schemas,
        schemasProviders: schemasProviders,
      },
    };

    return returnedObject;
  };

  const createOutputData = template => {
    let fields = [];

    if (template.configuration.refine) {
      Object.keys(template.configuration.refine).forEach(name => {
        fields.push({ name, log_flattened_name: name, connector: "refine" });
      });
    }

    template.table_providers.forEach(tp => {
      // output_fields aren't sent when not logged in
      (tp.version.output_fields || []).forEach(f => {
        fields.push({ ...f, connector: tp.alias || toNaturalSentence(tp.name) });
      });
    });

    // kind of hack, but <SchemaTable> expects this format since it's used for both providers
    // and recipes
    return { latest_version: { output_fields: fields } };
  };

  useEffect(() => {
    dispatch(
      userActions.setLastLoggedOutPage({
        lastLoggedOutPage: window.location.pathname,
      }),
    );
  }, [router, isUserLoggedIn]);

  useEffect(() => {
    const source = Axios.CancelToken.source();

    const getData = async () => {
      try {
        setError(null);
        setIsLoading(true);

        const [{ data }, { data: relatedRecipes }] = await Promise.all([
          apiTemplatesCatalogApi.getAPITemplate(templateId, {
            cancelToken: source.token,
          }),
          apiTemplatesCatalogApi.getRelatedRecipes(templateId, {
            cancelToken: source.token,
          }),
        ]);
        setRelatedRecipes(relatedRecipes);
        setTemplateData(data);

        const seoPath = `${window.location.pathname.split(templateId)[0]}${templateId}/${slugify(
          data.alias || toNaturalSentence(data.name),
        )}`;

        const seoTitle = `${data.alias || toNaturalSentence(data.name)} - API Recipe`;

        setCanonical(seoPath);
        setTitle(seoTitle);

        router.replace(seoPath);

        let inputs = [];
        let schemas = [];
        let schemasProviders = [];
        data.table_providers.forEach(provider => {
          if (notEmptyArray(provider.version.input_fields)) {
            const arrayOfInputs = provider.version.input_fields;
            arrayOfInputs.forEach(input => {
              const isPresent = inputs.findIndex(item => item.name === input.name);
              if (isPresent === -1) {
                inputs.push(input);
              }
            });
          }
          if (notEmptyArray(provider.version.schema)) {
            const arrayOfSchemas = provider.version.schema;
            schemas.push(arrayOfSchemas);
            schemasProviders.push({ name: provider.data_source.name, logo: provider.logo });
          }
        });
        const preparedInputs = createInputData({
          inputs,
          recommended: data.recommended_inputs,
          schemas,
          schemasProviders,
        });
        const preparedOutputs = createOutputData(data);
        setInputsData(preparedInputs);
        setOutputData(preparedOutputs);
      } catch (e) {
        setError(serializeError(e));
      } finally {
        setIsLoading(false);
      }
    };

    getData();

    return () => {
      source.cancel();
      setIsLoading(false);
    };
  }, [templateId]);

  // Guard for fake "Blank" recipe
  if (router.query.templateId === "69") {
    router.push(linksConstants.ALL_USE_CASES);
  }

  return (
    <div className="DataPageLayout">
      {isLoading ? (
        <div className="DataPageLayout__loading">
          <Loader />
        </div>
      ) : error ? (
        <div className="DataPageLayout__error">
          <PlatformHead title="An Error Occurred" />
          <MessageBox>Something went wrong, we are investigating</MessageBox>
          <LinkButton href={linksConstants.DATA_CATALOG_RECIPES}>Back to Catalog</LinkButton>
        </div>
      ) : Object.keys(templateData).length !== 0 ? (
        <>
          <PlatformHead
            title={title}
            canonical={canonical}
            description={templateData.description}
            pageType="product"
          />
          <DataPageHeader
            currentLocation={router.asPath}
            data={templateData}
            openCloneAPIModal={openCloneAPIModal}
            isUserLoggedIn={isUserLoggedIn}
            canCloneApiTemplates={canCloneApiTemplates}
          />
          <div className="DataPageLayout__container">
            <section className="DataPageLayout__info">
              <ApplicableRegulationsDescriptions item={templateData} />
              <DataPageAbout
                data={templateData}
                origin="API Templates"
                userData={userData}
                isUserLoggedIn={isUserLoggedIn}
              />
              <DataPageGridItems
                data={relatedRecipes}
                type="API Recipes"
                id={"dataPage-similar"}
                name="Similar Recipes"
              />
              {isUserLoggedIn && (
                <>
                  <SchemaTable type="api_template" data={outputData} />
                  <InputsTable product={inputsData} />
                  <section className="DataPageLayout__configurationContainer">
                    <p id="dataPage-configuration" className="DataPageLayout__sectionTitle">
                      Configuration
                    </p>
                    <CodeEditor
                      editorOptions={{
                        readOnly: true,
                      }}
                      defaultValue={JSON.stringify(templateData.configuration, null, 2)}
                    />
                  </section>
                </>
              )}
              {!isUserLoggedIn && <PublicBlock isOutputHidden={true} />}
            </section>
            <CloneAPIModal
              open={cloneAPIModalOpen}
              closeModal={closeCloneAPIModal}
              id={templateData.id}
              name={templateData.name}
              description={templateData.description}
              variant="FROM_TEMPLATES_CATALOG"
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
